import { resolveRiskKey } from './riskBands';
import { riskStateFromEmployee } from './employeeRiskState';
import { progressiveFallbackLabel } from './progressiveInsight';

/** True when turnover API returned a score safe to show as a number (not withheld). */
export function isTurnoverDisplayReady(turnover) {
  if (!turnover || typeof turnover !== 'object') return false;
  if (turnover.scoreReady === false) return false;
  return Number.isFinite(Number(turnover.score));
}

/** Band key for filters/charts, or null when score must not be bucketed. */
export function turnoverRiskBandKey(turnover) {
  if (!isTurnoverDisplayReady(turnover)) return null;
  return resolveRiskKey({ score: turnover.score, level: turnover.level });
}

/**
 * True only when backend/metadata explicitly marks active risk computation.
 */
const ACTIVE_PROCESSING_STATES = new Set(['processing', 'queued', 'retrying']);

export function isRiskActivelyProcessing(turnover) {
  if (!turnover || typeof turnover !== 'object') return false;
  if (isTurnoverDisplayReady(turnover)) return false;
  const state = String(turnover?.metadata?.processingState || '').toLowerCase();
  return ACTIVE_PROCESSING_STATES.has(state);
}

export function isRiskMaterializationFailed(turnover, employee = null) {
  if (!turnover || typeof turnover !== 'object') return false;
  if (isTurnoverDisplayReady(turnover)) return false;
  const state = String(turnover?.metadata?.processingState || '').toLowerCase();
  if (state === 'failed') return true;
  if (employee) {
    const rs = riskStateFromEmployee(employee);
    if (rs?.processingState === 'failed') return true;
  }
  return false;
}

export function turnoverProcessingState(turnover) {
  if (!turnover || typeof turnover !== 'object') return 'not_calculated';
  if (isTurnoverDisplayReady(turnover)) return 'calculated';
  if (isRiskActivelyProcessing(turnover)) return 'processing';
  const state = String(turnover?.metadata?.processingState || '').toLowerCase();
  if (state === 'insufficient_data') return 'insufficient_data';
  if (state === 'calculated' || state === 'ready') return 'calculated';
  if (state === 'pending') return 'not_calculated';
  return 'not_calculated';
}

/** User-facing blocked reason from persisted payload (never generic when specific cause exists). */
export function turnoverBlockedMessage(turnover, employee = null) {
  if (!turnover || isTurnoverDisplayReady(turnover)) return null;
  const fromMeta = turnover?.metadata?.displayBlockedReason;
  if (fromMeta && String(fromMeta).trim()) return String(fromMeta).trim();
  if (turnover?.summary && String(turnover.summary).trim()) return String(turnover.summary).trim();
  if (employee) {
    const rs = riskStateFromEmployee(employee);
    if (rs?.displayBlockedReason) return rs.displayBlockedReason;
  }
  return null;
}

/**
 * Canonical UI phase for risk display surfaces.
 * @returns {'ready'|'loading'|'empty'|'blocked'|'partial'}
 */
export function turnoverRiskUiPhase(turnover, employee = null) {
  if (isTurnoverDisplayReady(turnover)) return 'ready';
  if (isRiskActivelyProcessing(turnover)) return 'loading';
  if (isRiskMaterializationFailed(turnover, employee)) return 'failed';
  const blocked = turnoverBlockedMessage(turnover, employee);
  if (blocked) return 'blocked';
  if (employee) return 'partial';
  if (shouldShowNotEnoughDataFallback(turnover, employee)) return 'blocked';
  return 'partial';
}

/**
 * Copy for list/profile reason columns — never implies active work unless processing.
 */
export function turnoverReasonColumnCopy(turnover, employee = null) {
  if (isTurnoverDisplayReady(turnover)) return null;
  const blocked = turnoverBlockedMessage(turnover, employee);
  if (blocked) return blocked;
  if (isRiskActivelyProcessing(turnover)) {
    return 'Risk analysis in progress';
  }
  return progressiveFallbackLabel(employee, turnover, null, 'top_risk_driver');
}

/**
 * processingState prop for RiskScore — only 'processing' when work is active.
 */
export function turnoverRiskScoreProcessingProp(turnover, employee = null) {
  if (isRiskActivelyProcessing(turnover)) return 'processing';
  if (isRiskMaterializationFailed(turnover, employee)) return 'failed';
  return null;
}

/** Freshness SLA UI status from canonical riskState. */
export function turnoverFreshnessStatus(turnover, employee = null) {
  if (employee?.riskState?.freshnessStatus) return employee.riskState.freshnessStatus;
  if (employee) {
    const rs = riskStateFromEmployee(employee);
    if (rs?.freshnessStatus) return rs.freshnessStatus;
  }
  return null;
}

export function confidenceDisplayLabel(level) {
  const l = String(level || '').toLowerCase();
  if (l === 'high') return 'High confidence';
  if (l === 'medium') return 'Medium confidence';
  return null;
}

export function turnoverConfidenceLevel(turnover, employee = null) {
  if (turnover?.confidenceLevel) return turnover.confidenceLevel;
  if (turnover?.metadata?.confidenceLevel) return turnover.metadata.confidenceLevel;
  if (employee) {
    const rs = riskStateFromEmployee(employee);
    if (rs?.confidenceLevel) return rs.confidenceLevel;
  }
  return null;
}

export function turnoverConfidenceNote(turnover, employee = null) {
  if (turnover?.confidenceNote) return turnover.confidenceNote;
  if (employee) {
    const rs = riskStateFromEmployee(employee);
    if (rs?.confidenceNote) return rs.confidenceNote;
  }
  return null;
}

export function shouldShowNotEnoughDataFallback(turnover, employee = null) {
  if (isTurnoverDisplayReady(turnover)) return false;
  if (isRiskActivelyProcessing(turnover)) return false;
  if (employee) return false;
  const blocked = turnoverBlockedMessage(turnover, employee);
  return Boolean(blocked);
}
